import React from 'react';
import { UseQueryResult } from 'react-query';
import { useGlobalState } from '@creditas/global-store';
import { useGetApplicationStatus } from '../../hooks/queries/useGetApplicationStatus';
import {
  useGetAccountInformation,
  AccountInformation,
} from '../../hooks/queries/useGetAccountInformation';
import { ApplicationStatus, Applications } from '../../services/getApplicationStatus';
import ballonImg from '../../assets/balloon.webp';
import homeImg from '../../assets/home.webp';
import {
  dispatchSpotlightStatusActiveEvent,
  dispatchSpotlightStatusDefaultEvent,
} from '../../utils/analyticsEvents';

enum SpotlightStatus {
  ACTIVE = 'active',
  DEFAULT = 'default',
}

type SpotlightProps = {
  translationKey: string;
  name: string;
  image: string;
};

export type UseSpotlightResult = {
  status: SpotlightStatus | null;
  isSuccess: boolean;
  spotlightProps: SpotlightProps;
  applicationStatus: UseQueryResult<Applications, Error>;
  accountInformation: UseQueryResult<AccountInformation, Error>;
};

const ImageByStatus = {
  [SpotlightStatus.ACTIVE]: homeImg,
  [SpotlightStatus.DEFAULT]: ballonImg,
};

const dispatchLoadEventByStatus = {
  [SpotlightStatus.ACTIVE]: dispatchSpotlightStatusActiveEvent,
  [SpotlightStatus.DEFAULT]: dispatchSpotlightStatusDefaultEvent,
};

const getFirstName = (fullName: string): string => fullName.split(' ')[0];

const getSpotlightStatus = ({ applications }: Applications = {}): SpotlightStatus => {
  if (
    applications?.AUTO?.status === ApplicationStatus.OPEN ||
    applications?.HOME?.status === ApplicationStatus.OPEN
  ) {
    return SpotlightStatus.ACTIVE;
  }

  return SpotlightStatus.DEFAULT;
};

const useSpotlight = (): UseSpotlightResult => {
  const state = useGlobalState();
  const [status, setStatus] = React.useState<SpotlightStatus | null>(null);
  const applicationStatus = useGetApplicationStatus();
  const accountInformation = useGetAccountInformation();

  React.useEffect(() => {
    const currentStatus = getSpotlightStatus(applicationStatus?.data);

    if (applicationStatus.isSuccess && currentStatus !== status) {
      setStatus(currentStatus);
      dispatchLoadEventByStatus[currentStatus]();
    }
  }, [applicationStatus, status]);

  React.useEffect(() => {
    if (!applicationStatus.isLoading && !accountInformation.isLoading) {
      state?.dashboardLoader?.toggleSpotlight?.();
    }
  }, [applicationStatus.isLoading, accountInformation.isLoading]);

  return {
    status,
    isSuccess: applicationStatus.isSuccess && accountInformation.isSuccess,
    spotlightProps: {
      translationKey: status || SpotlightStatus.DEFAULT,
      name: getFirstName(accountInformation?.data?.screenName ?? ''),
      image: ImageByStatus[status || SpotlightStatus.DEFAULT],
    },
    applicationStatus,
    accountInformation,
  };
};

export { useSpotlight, SpotlightStatus };
